import type { UIStateAPI } from "@/types";
import { zCurrentBreakpoint } from "@/data/schemas";

interface BreakpointAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

export const hydrateBreakpointFromSession = (
  deps: Pick<BreakpointAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
) => {
  if (!screen) return;
  const { store, getPath } = deps;
  const bPath = getPath("currentBreakpoint");
  if (!bPath) return;
  try {
    store.registerSchema(bPath, zCurrentBreakpoint);
  } catch {}
  if (typeof (screen as any).currentBreakpoint === "string") {
    store.set(bPath, (screen as any).currentBreakpoint as any, {
      source: "hydration",
    });
  }
};

export const registerBreakpointAdapter = (
  deps: BreakpointAdapterDeps
): (() => void) => {
  const { store, uiState, eventBus, persist } = deps;
  const unsubscribes: (() => void)[] = [];

  // Debug toggles
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.breakpoint || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("breakpoint");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:breakpoint]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "breakpoint") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  const unsubscribeStoreBreakpoint = store.subscribe(
    (path: string) => path.endsWith(".screenState.currentBreakpoint"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      try {
        if (typeof value === "string") (uiState as any).setBreakpoint(value);
        debug("store→UI breakpoint", { value, source });
      } catch {}
      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreBreakpoint);

  // UIState -> Store (currentBreakpoint)
  const unsubscribeUIStateChanged = eventBus.on(
    "ui-state-changed",
    (data: any) => {
      try {
        const bp = data?.state?.current_breakpoint;
        const bPath = (deps.getPath as any)("currentBreakpoint") as
          | string
          | null;
        if (!bPath) return;
        if (typeof bp === "string") {
          try {
            store.registerSchema(bPath, zCurrentBreakpoint);
          } catch {}
          debug("UI→store breakpoint", { bp });
          store.set(bPath, bp as any, { source: "breakpointAdapter" as any });
        }
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeUIStateChanged);

  const cleanup = () => {
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };
  try {
    const unsub = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsub);
  } catch {}
  return cleanup;
};
